/**
 * 시뮬레이션(저작 미리보기)의 검색 제안 어댑터 — web 호스트 어댑터의 admin 대응물.
 *
 * 저작자가 `suggestionsFrom`을 걸어 놓고 **시뮬레이션에서 그 설정을 확인**할 수 있어야 한다
 * (배포 작성 화면과 같은 축을 미리 본다는 게 이 페이지의 존재 이유). web과 같은 데이터를 보되
 * 호출 경로만 admin(super admin 가드)으로 바꾼다:
 *  - 대책 라이브러리: `/admin/knowledge` — 지식 항목 목록이라 내부 `measures[]`를 평탄화한다
 *    (web의 조직 스코프 `/organizations/:id/knowledge`가 이미 대책 단위로 주는 것과 형태가 다름).
 *  - 법령 조문: `/admin/regulations/search` — 규정 검색.
 *
 * ⚠️ **부수효과 0**: 시뮬레이션은 `onCatalogRowSubmit`도 로컬 합성으로 끝낸다(서버 write 없음).
 * 같은 원칙으로 여기서도 **선택 통지(onSuggestSelect)를 배선하지 않는다** — 미리보기에서 고른
 * 대책이 실제 usage 통계를 올리면 저작 행위가 운영 데이터를 오염시킨다. 검색은 읽기라 무해.
 *
 * 결정: docs/decisions/2026-08-05-composer-suggestion-source-axis.md
 */
import {
  LAW_ARTICLE_SUGGESTION_SOURCE_KEY,
  MEASURE_SUGGESTION_SOURCE_KEY,
  formatLegalBasisArticleRows,
  formatLegalBasisDetailBlocks,
  type ComposerSuggestSearch,
  type ComposerSuggestionRow,
} from '@safetyhub/shared';
import { adminApi } from '../../lib/api';

/** 제안 상한 — web 어댑터와 같은 값(목록이 길면 읽히지 않는다). 단위는 **제안 1건**. */
const SUGGEST_LIMIT = 10;

/**
 * 대책 라이브러리 조회 폭 — `/admin/knowledge`는 **지식 항목** 단위 페이지네이션이라 상한의 단위가
 * web 어댑터(대책 단위)와 다르다. 항목 수를 그대로 10으로 잡으면 항목마다 대책이 여럿일 때
 * 앞쪽 2~3항목의 대책만으로 10칸이 차서, 같은 검색어인데 시뮬레이션과 배포 작성 화면의 결과가
 * 갈린다. 그래서 항목은 넉넉히 받아 평탄화한 뒤 대책 단위로 자른다(서버 상한 50 이내).
 *
 * ⚠️ 그래도 **완전히 같아지지는 않는다**: 서버 정렬은 지식 항목 단위 usageCount라, 이 창 밖의
 * 항목에 딸린 고사용 대책은 여기 안 들어온다. 제안 목록의 성격(상위 몇 건 훑기)상 감수한다.
 */
const KNOWLEDGE_FETCH_LIMIT = 30;

/** 필터 값 정리 — 공백뿐이면 필터 없음(전체 검색). */
function filterValue(filter: Record<string, string> | undefined, key: string): string | undefined {
  const v = filter?.[key]?.trim();
  return v ? v : undefined;
}

/** 분류 체인 — 제안 배지(중분류 없으면 대분류만). */
function categoryChain(category: string, subCategory: string | null): string | undefined {
  const chain = subCategory ? `${category} > ${subCategory}` : category;
  return chain || undefined;
}

/**
 * 대책 라이브러리 검색(admin 경로) — 응답이 **지식 항목** 목록이라 항목마다 딸린 `measures[]`를
 * 평탄화해 대책 단위 제안으로 만든다. raw는 web 어댑터(Measure)와 같은 필드 이름을 맞춰
 * 저작해 둔 writes 매핑(`measure`/`category`/`id`…)이 두 화면에서 같게 동작하게 한다.
 *
 * 상한의 **단위 차이**(항목 vs 대책)는 KNOWLEDGE_FETCH_LIMIT 주석 참조 — 조회는 항목 단위로
 * 넓게, 최종 자르기는 web과 같은 대책 단위 10건으로 한다.
 */
export async function searchMeasureSuggestionsForSimulation(
  query: string,
  filter?: Record<string, string>,
  companyId?: string,
): Promise<ComposerSuggestionRow[]> {
  const res = await adminApi.getMeasures({
    search: query,
    category: filterValue(filter, 'category'),
    subCategory: filterValue(filter, 'subCategory'),
    organizationId: companyId || undefined,
    // 지식 항목 단위 상한(대책 단위 아님 — KNOWLEDGE_FETCH_LIMIT 주석 참조).
    limit: KNOWLEDGE_FETCH_LIMIT,
    sortBy: 'usageCount',
    sortOrder: 'desc',
  });
  // adminApi는 { success, data, error } 래퍼 — 목록은 data.data(페이지네이션 봉투 안).
  const rows: ComposerSuggestionRow[] = [];
  for (const knowledge of res.data?.data ?? []) {
    for (const m of knowledge.measures ?? []) {
      rows.push({
        text: m.content,
        badge: categoryChain(knowledge.category, knowledge.subCategory),
        raw: {
          id: m.id,
          measure: m.content,
          category: knowledge.category,
          subCategory: knowledge.subCategory,
          source: knowledge.source,
          usageCount: m.usageCount,
        },
      });
    }
  }
  return rows.slice(0, SUGGEST_LIMIT);
}

/**
 * 법령 조문 검색(admin 경로) — 제안 본문은 web과 같은 **표시 정본**
 * (`formatLegalBasisDetailBlocks`)으로 만든다. 저작자가 시뮬에서 고른 조문과 배포 작성 화면에서
 * 고른 조문이 같은 형식이어야 미리보기가 미리보기 구실을 한다.
 */
export async function searchLawSuggestionsForSimulation(
  query: string,
): Promise<ComposerSuggestionRow[]> {
  // ⚠️ `adminApi.searchRegulations`(`/admin/regulations/search`)는 서버에 없는 죽은 클라이언트
  // 메서드다(2026-08-05 실측 404). 실제 검색 라우트는 목록 엔드포인트의 search 파라미터다.
  const res = await adminApi.getRegulations({
    search: query,
    limit: SUGGEST_LIMIT,
    isActive: true,
  });
  return (res.data?.data ?? []).map((r) => {
    // ⚠️ admin 목록은 조문 번호를 **code**('제418조')에 담고 `article`은 호(sub-item) 자리라 보통
    // null이다 — web의 legal-bases(article에 조문 번호)와 필드 이름이 어긋난다. 그대로 넘기면
    // 제안 본문에서 조문 번호가 사라져 배포 화면과 형식이 갈린다(2026-08-05 실측).
    const articleNo = r.article ?? r.code ?? undefined;
    // ⚠️ 본문이 **항에만** 있는 조문이 실재한다(전기안전관리법 제43조 등 — summary가 빈 문자열).
    // paragraphs를 넘기지 않으면 표시 정본이 합성할 재료가 없어 제안이 헤더만 남는다.
    const item = {
      type: r.type,
      article: articleNo,
      title: r.title ?? undefined,
      summary: r.summary ?? undefined,
      paragraphs: r.paragraphs,
      url: r.url ?? undefined,
    };
    const [block] = formatLegalBasisDetailBlocks([item]);
    const [articleRow] = formatLegalBasisArticleRows([item]);
    return {
      text: block ?? [articleNo, r.title].filter(Boolean).join(' '),
      badge: r.type || undefined,
      raw: {
        id: r.id,
        type: r.type,
        code: r.code,
        // raw도 web 어댑터(LegalBasis)와 같은 이름으로 맞춘다 — 저작한 writes 매핑이 두 화면에서
        // 같게 동작해야 미리보기가 미리보기 구실을 한다.
        article: articleNo,
        title: r.title,
        summary: r.summary,
        paragraphs: r.paragraphs,
        url: r.url,
        // 조문 단위 표(법령/조문 두 칼럼) 전파 source — 합본 블록에는 법령명 머리글이 붙어 있어
        // 조문 칼럼에 그대로 넣을 수 없다(web 어댑터와 같은 이름·같은 정본).
        lawName: articleRow?.lawName ?? '',
        articleText: articleRow?.articleText ?? '',
      },
    };
  });
}

/** 시뮬레이션 검색 콜백(rc 계약) — 지원 소스만 조회, 미지원 키는 빈 배열 degrade. */
export function createSimulationSuggestSearch(companyId?: string): ComposerSuggestSearch {
  return async (catalogKey, query, filter) => {
    if (catalogKey === MEASURE_SUGGESTION_SOURCE_KEY) {
      return searchMeasureSuggestionsForSimulation(query, filter, companyId);
    }
    if (catalogKey === LAW_ARTICLE_SUGGESTION_SOURCE_KEY) {
      return searchLawSuggestionsForSimulation(query);
    }
    return [];
  };
}
